import { useTranslation } from 'react-i18next';
import { SectionPanel } from '~/components/ui/SectionPanel';
import {
  statusBadgeClasses,
  type StatusBadgeTone,
} from '~/components/ui/statusBadgeClasses';
import { cn, formatDateOnly, hasText } from '~/utils';
import { useIncotermLabel } from '~/utils/incoterms';
import {
  MsrFieldEditability,
  type MsrPurchaseOrder,
  type MsrPurchaseOrderField,
  type MsrPurchaseOrderInputsView,
  type MsrPurchaseOrderItem,
} from '~/graphql/graphql';
import {
  formatMsrValue,
  groupMsrFields,
  msrFieldLabel,
} from './msrPurchaseOrderFields';

const EDITABILITIES = [
  MsrFieldEditability.BuyerInput,
  MsrFieldEditability.FromOrder,
  MsrFieldEditability.Calculated,
  MsrFieldEditability.MsrLimitation,
] as const;

const TAG_TONES: Record<MsrFieldEditability, StatusBadgeTone> = {
  BUYER_INPUT: 'shipped',
  FROM_ORDER: 'info',
  CALCULATED: 'accepted',
  MSR_LIMITATION: 'warning',
};

function EditabilityTag({
  editability,
}: Readonly<{ editability: MsrFieldEditability }>) {
  const { t } = useTranslation('order');
  return (
    <span
      data-editability={editability}
      className={cn('inline-block', statusBadgeClasses(TAG_TONES[editability]))}
    >
      {t(`order.msr_purchase_order.editability.${editability}.tag`)}
    </span>
  );
}

function fieldValue(fields: readonly MsrPurchaseOrderField[], key: string) {
  const match = fields.find((field) => field.key === key);
  return match != null ? (match.value ?? null) : null;
}

function MsrFieldRow({
  field,
  scope,
  currency,
}: Readonly<{
  field: MsrPurchaseOrderField;
  scope: 'header' | 'item';
  currency: string | null;
}>) {
  const { t } = useTranslation('order');
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 text-sm">
      <dt className="text-neut-sec">{msrFieldLabel(field.key, scope, t)}</dt>
      <dd className="flex items-baseline gap-2 text-right text-neut-prim">
        <span className="break-words">
          {formatMsrValue(field.key, field.value, { currency, t })}
        </span>
        <EditabilityTag editability={field.editability} />
      </dd>
    </div>
  );
}

function MsrItem({
  item,
  index,
  currency,
}: Readonly<{
  item: MsrPurchaseOrderItem;
  index: number;
  currency: string | null;
}>) {
  const line =
    fieldValue(item.fields, 'purchaseOrderItemNumber') ?? String(index + 1);
  const title = [
    line,
    fieldValue(item.fields, 'productCode'),
    fieldValue(item.fields, 'productDescription'),
  ]
    .filter((part) => part != null && part !== '')
    .join(' · ');

  return (
    <li
      data-testid="msr-purchase-order-item"
      className="rounded border border-neut-border p-3"
    >
      <p className="text-sm font-medium text-neut-prim">{title}</p>
      <dl className="mt-2 divide-y divide-neut-border">
        {item.fields.map((field) => (
          <MsrFieldRow
            key={field.key}
            field={field}
            scope="item"
            currency={currency}
          />
        ))}
      </dl>
    </li>
  );
}

function Legend() {
  const { t } = useTranslation('order');
  return (
    <div data-testid="msr-purchase-order-legend" className="mb-4">
      <SectionPanel title={t('order.msr_purchase_order.legend_title')}>
        <ul className="space-y-1">
          {EDITABILITIES.map((editability) => (
            <li key={editability} className="flex items-baseline gap-2 text-sm">
              <EditabilityTag editability={editability} />
              <span className="text-neut-sec">
                {t(
                  `order.msr_purchase_order.editability.${editability}.reason`
                )}
              </span>
            </li>
          ))}
        </ul>
      </SectionPanel>
    </div>
  );
}

function ChoiceRow({
  label,
  value,
}: Readonly<{ label: string; value: string }>) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 text-sm">
      <dt className="text-neut-sec">{label}</dt>
      <dd className="text-right text-neut-prim break-words">{value}</dd>
    </div>
  );
}

function CheckoutChoices({
  choices,
}: Readonly<{ choices: MsrPurchaseOrderInputsView }>) {
  const { t } = useTranslation('order');
  const base = 'order.msr_purchase_order.checkout_choices';
  const incotermLabel = useIncotermLabel();
  const notSet = t('order.msr_purchase_order.not_set');
  const yesNo = (value: boolean | null | undefined) => {
    if (value == null) return notSet;
    return t(value ? 'order.yes' : 'order.no');
  };
  return (
    <div data-testid="msr-checkout-choices" className="mb-4">
      <SectionPanel title={t(`${base}.title`)} bodyClassName="px-4">
        <dl className="divide-y divide-neut-border">
          <ChoiceRow
            label={t(`${base}.buyer_code`)}
            value={choices.buyerCode ?? t(`${base}.buyer_code_default`)}
          />
          <ChoiceRow
            label={t(`${base}.ordered_by_code`)}
            value={choices.orderedByCode ?? notSet}
          />
          <ChoiceRow
            label={t(`${base}.cash_contract`)}
            value={yesNo(choices.cashContract)}
          />
          <ChoiceRow
            label={t(`${base}.num_days_intransit`)}
            value={
              choices.numDaysIntransit == null
                ? notSet
                : String(choices.numDaysIntransit)
            }
          />
          <ChoiceRow
            label={t(`${base}.unit_type`)}
            value={choices.unitType ?? notSet}
          />
          <ChoiceRow
            label={t(`${base}.is_import`)}
            value={yesNo(choices.isImport)}
          />
          <ChoiceRow
            label={t(`${base}.ready_at_mill`)}
            value={
              choices.readyAtMill == null
                ? notSet
                : formatDateOnly(choices.readyAtMill)
            }
          />
          <ChoiceRow
            label={t(`${base}.inco_terms`)}
            value={
              choices.incoTerms == null
                ? notSet
                : incotermLabel(choices.incoTerms)
            }
          />
          <ChoiceRow
            label={t(`${base}.inco_terms_loc`)}
            value={
              hasText(choices.incoTermsLoc) ? choices.incoTermsLoc : notSet
            }
          />
        </dl>
      </SectionPanel>
    </div>
  );
}

function MsrPurchaseOrderDetails({
  purchaseOrder,
}: Readonly<{ purchaseOrder: MsrPurchaseOrder }>) {
  const { t } = useTranslation('order');
  const currency = fieldValue(purchaseOrder.fields, 'currency');
  const groups = groupMsrFields(purchaseOrder.fields);

  return (
    <>
      <Legend />
      <div className="space-y-4">
        {groups.map(({ group, fields }) => (
          <div key={group} data-testid={`msr-group-${group}`}>
            <SectionPanel
              title={t(`order.msr_purchase_order.groups.${group}`)}
              bodyClassName="px-4"
            >
              <dl className="divide-y divide-neut-border">
                {fields.map((field) => (
                  <MsrFieldRow
                    key={field.key}
                    field={field}
                    scope="header"
                    currency={currency}
                  />
                ))}
              </dl>
            </SectionPanel>
          </div>
        ))}
        {purchaseOrder.items.length > 0 && (
          <SectionPanel title={t('order.msr_purchase_order.items_title')}>
            <ul className="space-y-3">
              {purchaseOrder.items.map((item, index) => (
                <MsrItem
                  key={
                    fieldValue(item.fields, 'purchaseOrderItemNumber') ?? index
                  }
                  item={item}
                  index={index}
                  currency={currency}
                />
              ))}
            </ul>
          </SectionPanel>
        )}
      </div>
    </>
  );
}

export function MsrPurchaseOrderCard({
  purchaseOrder,
  checkoutChoices = null,
}: Readonly<{
  purchaseOrder: MsrPurchaseOrder | null;
  checkoutChoices?: MsrPurchaseOrderInputsView | null;
}>) {
  const { t } = useTranslation('order');
  const hasDetails =
    purchaseOrder != null &&
    (purchaseOrder.fields.length > 0 || purchaseOrder.items.length > 0);

  return (
    <div data-testid="msr-purchase-order">
      <SectionPanel title={t('order.msr_purchase_order.title')}>
        {checkoutChoices != null && (
          <CheckoutChoices choices={checkoutChoices} />
        )}
        {hasDetails ? (
          <MsrPurchaseOrderDetails purchaseOrder={purchaseOrder} />
        ) : (
          <p className="text-sm text-neut-sec">
            {t('order.msr_purchase_order.empty')}
          </p>
        )}
      </SectionPanel>
    </div>
  );
}
